import { computed, inject, Injectable, PLATFORM_ID, signal } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import {
  AccountRecoveryProgress,
  City,
  FlowType,
  ManagerRequestForm,
  RestorePath,
} from '../interfaces/restore.interfaces';
import { DriverTicketResource } from '../interfaces/driver-ticket.interfaces';

const ACCOUNT_RECOVERY_PROGRESS_KEY = 'account_recovery_progress';
const ACCOUNT_RECOVERY_SUBMITTED_TICKET_KEY = 'account_recovery_submitted_ticket_id';

function emptyAccountRecoveryProgress(): AccountRecoveryProgress {
  return { ticketId: null, form: null, phoneVerified: false, verificationChannel: 'sms' };
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function isManagerRequestForm(value: unknown): value is ManagerRequestForm {
  if (!isRecord(value)) return false;

  const city = value['city'];
  const validCity =
    city === null ||
    (isRecord(city) &&
      (typeof city['id'] === 'string' || typeof city['id'] === 'number') &&
      typeof city['name'] === 'string' &&
      (city['code'] === undefined || typeof city['code'] === 'string'));

  return (
    validCity &&
    typeof value['registeredPhone'] === 'string' &&
    typeof value['contactPhone'] === 'string' &&
    typeof value['callSign'] === 'string' &&
    typeof value['birthDate'] === 'string' &&
    typeof value['lastName'] === 'string' &&
    typeof value['firstName'] === 'string' &&
    typeof value['middleName'] === 'string'
  );
}

function isAccountRecoveryProgress(value: unknown): value is AccountRecoveryProgress {
  return (
    isRecord(value) &&
    typeof value['phoneVerified'] === 'boolean' &&
    (value['form'] === null || isManagerRequestForm(value['form'])) &&
    (value['verificationChannel'] === undefined ||
      value['verificationChannel'] === 'sms' ||
      value['verificationChannel'] === 'call')
  );
}

const KEYS = {
  token: 'restore_token',
  serviceId: 'restore_service_id',
  path: 'restore_path',
  applicationId: 'restore_application_id',
  fcm: 'restore_fcm',
  flowType: 'flow_type',
  mainPhone: 'apn_main_phone',
  additionalPhone: 'apn_additional_phone',
  newPhone: 'cpn_new_phone',
  ticketId: 'apn_ticket_id',
  mainVerificationRequestId: 'apn_main_verification_request_id',
  additionalVerificationRequestId: 'apn_additional_verification_request_id',
  accountRecoveryTicketId: 'account_recovery_ticket_id',
  lastMileage: 'rent_last_mileage',
} as const;

@Injectable({ providedIn: 'root' })
export class RestoreSessionService {
  private platformId = inject(PLATFORM_ID);
  private isBrowser = isPlatformBrowser(this.platformId);

  private getItem(key: string): string | null {
    if (!this.isBrowser) return null;

    try {
      return sessionStorage.getItem(key);
    } catch {
      return null;
    }
  }

  private setItem(key: string, value: string): void {
    if (!this.isBrowser) return;

    try {
      sessionStorage.setItem(key, value);
    } catch {
      // Storage can be unavailable in restricted browser contexts.
    }
  }

  private removeItem(key: string): void {
    if (!this.isBrowser) return;

    try {
      sessionStorage.removeItem(key);
    } catch {
      // Storage can be unavailable in restricted browser contexts.
    }
  }

  private getLocalItem(key: string): string | null {
    if (!this.isBrowser) return null;

    try {
      return localStorage.getItem(key);
    } catch {
      return null;
    }
  }

  private setLocalItem(key: string, value: string): void {
    if (!this.isBrowser) return;

    try {
      localStorage.setItem(key, value);
    } catch {
      // Storage can be unavailable in restricted browser contexts.
    }
  }

  private removeLocalItem(key: string): void {
    if (!this.isBrowser) return;

    try {
      localStorage.removeItem(key);
    } catch {
      // Storage can be unavailable in restricted browser contexts.
    }
  }

  private removeOtherAccountRecoveryTicketKeys(applicationId: string | null): void {
    if (!this.isBrowser) return;

    const currentKey = this.accountRecoveryTicketStorageKey(applicationId);
    const keyPrefix = `${KEYS.accountRecoveryTicketId}:`;
    try {
      const keysToRemove: string[] = [];
      for (let index = 0; index < localStorage.length; index += 1) {
        const key = localStorage.key(index);
        if (key?.startsWith(keyPrefix) && key !== currentKey) keysToRemove.push(key);
      }
      keysToRemove.forEach((key) => localStorage.removeItem(key));
      localStorage.removeItem(KEYS.accountRecoveryTicketId);
    } catch {
      // Storage can be unavailable in restricted browser contexts.
    }
  }

  private accountRecoveryTicketStorageKey(applicationId: string | null): string | null {
    return applicationId
      ? `${KEYS.accountRecoveryTicketId}:${encodeURIComponent(applicationId)}`
      : null;
  }

  private clearPersistedAccountRecoveryState(applicationId: string | null): void {
    const ticketStorageKey = this.accountRecoveryTicketStorageKey(applicationId);
    if (ticketStorageKey) this.removeLocalItem(ticketStorageKey);
    // Remove the pre-scoping key so an old origin-global ticket can never be resumed.
    this.removeLocalItem(KEYS.accountRecoveryTicketId);
    this.clearPersistedAccountRecoveryProgress();
  }

  private clearPersistedAccountRecoveryProgress(): void {
    this.removeAccountRecoveryProgressItem();
    this.removeItem(ACCOUNT_RECOVERY_SUBMITTED_TICKET_KEY);
    this.accountRecoverySubmittedTicketId?.set(null);
  }

  private readAccountRecoveryTicketId(): string | null {
    const applicationId = this.getItem(KEYS.applicationId);
    const ticketStorageKey = this.accountRecoveryTicketStorageKey(applicationId);
    const legacyTicketId = this.getLocalItem(KEYS.accountRecoveryTicketId);
    if (legacyTicketId) {
      this.clearPersistedAccountRecoveryState(applicationId);
      return null;
    }

    return ticketStorageKey ? this.getLocalItem(ticketStorageKey) : null;
  }

  private readAccountRecoveryProgress(): AccountRecoveryProgress {
    if (!this.isBrowser) return emptyAccountRecoveryProgress();

    try {
      const stored = sessionStorage.getItem(ACCOUNT_RECOVERY_PROGRESS_KEY);
      if (!stored) return emptyAccountRecoveryProgress();
      const parsed: unknown = JSON.parse(stored);
      const ticketStorageKey = this.accountRecoveryTicketStorageKey(
        this.getItem(KEYS.applicationId),
      );
      const restoredTicketId = ticketStorageKey ? this.getLocalItem(ticketStorageKey) : null;
      if (
        !isAccountRecoveryProgress(parsed) ||
        !Object.prototype.hasOwnProperty.call(parsed, 'ticketId') ||
        (parsed['ticketId'] !== null && typeof parsed['ticketId'] !== 'string') ||
        !restoredTicketId ||
        parsed['ticketId'] !== restoredTicketId
      ) {
        this.clearPersistedAccountRecoveryProgress();
        return emptyAccountRecoveryProgress();
      }
      return {
        ticketId: parsed['ticketId'],
        form: parsed['form'],
        phoneVerified: parsed['phoneVerified'],
        verificationChannel: parsed['verificationChannel'] ?? 'sms',
      };
    } catch {
      this.removeAccountRecoveryProgressItem();
      return emptyAccountRecoveryProgress();
    }
  }

  private persistAccountRecoveryProgress(progress: AccountRecoveryProgress): void {
    if (!this.isBrowser) return;

    try {
      sessionStorage.setItem(ACCOUNT_RECOVERY_PROGRESS_KEY, JSON.stringify(progress));
    } catch {
      // Storage can be unavailable in restricted browser contexts.
    }
  }

  private removeAccountRecoveryProgressItem(): void {
    if (!this.isBrowser) return;

    try {
      sessionStorage.removeItem(ACCOUNT_RECOVERY_PROGRESS_KEY);
    } catch {
      // Storage can be unavailable in restricted browser contexts.
    }
  }

  readonly token = signal<string | null>(this.getItem(KEYS.token));
  readonly serviceId = signal<string | null>(this.getItem(KEYS.serviceId));
  readonly applicationId = signal<string | null>(this.getItem(KEYS.applicationId));
  readonly fcm = signal<string | null>(this.getItem(KEYS.fcm));
  readonly flowType = signal<FlowType | null>(this.getItem(KEYS.flowType) as FlowType | null);
  readonly selectedPath = signal<RestorePath | null>(this.getItem(KEYS.path) as RestorePath | null);
  readonly selectedCity = signal<City | null>(null);
  readonly registeredPhone = signal<string | null>(null);
  readonly contactPhone = signal<string | null>(null);
  readonly formData = signal<ManagerRequestForm | null>(null);
  readonly mainPhone = signal<string | null>(this.getItem(KEYS.mainPhone));
  readonly additionalPhone = signal<string | null>(this.getItem(KEYS.additionalPhone));
  readonly newPhone = signal<string | null>(this.getItem(KEYS.newPhone));
  readonly ticketId = signal<string | null>(this.getItem(KEYS.ticketId));
  readonly mainVerificationRequestId = signal<string | null>(
    this.getItem(KEYS.mainVerificationRequestId),
  );
  readonly additionalVerificationRequestId = signal<string | null>(
    this.getItem(KEYS.additionalVerificationRequestId),
  );
  readonly accountRecoveryTicketId = signal<string | null>(this.readAccountRecoveryTicketId());
  readonly accountRecoverySubmittedTicketId = signal<string | null>(
    this.getItem(ACCOUNT_RECOVERY_SUBMITTED_TICKET_KEY),
  );
  readonly accountRecoveryProgress = signal<AccountRecoveryProgress>(
    this.readAccountRecoveryProgress(),
  );
  readonly lastMileage = signal<number | null>(this.readLastMileage());

  readonly hasSession = computed(() => !!this.token());

  /**
   * In-memory only (not persisted): holds a ticket already fetched by an intro
   * "continue existing ticket" flow so the next page doesn't re-fetch it via API.
   */
  private cachedTicket = signal<DriverTicketResource | null>(null);

  setCachedTicket(ticket: DriverTicketResource): void {
    this.cachedTicket.set(ticket);
  }

  consumeCachedTicket(ticketId: string): DriverTicketResource | null {
    const ticket = this.cachedTicket();
    if (ticket && ticket.id === ticketId) {
      this.cachedTicket.set(null);
      return ticket;
    }
    return null;
  }

  setToken(value: string): void {
    this.token.set(value);
    this.setItem(KEYS.token, value);
  }

  clearToken(): void {
    this.token.set(null);
    this.removeItem(KEYS.token);
  }

  setServiceId(value: string): void {
    this.serviceId.set(value);
    this.setItem(KEYS.serviceId, value);
  }

  setApplicationId(value: string): void {
    const previousApplicationId = this.applicationId();
    if (this.flowType() === 'account_recovery' && previousApplicationId !== value) {
      this.clearPersistedAccountRecoveryState(previousApplicationId);
      this.accountRecoveryTicketId.set(null);
      this.accountRecoveryProgress.set(emptyAccountRecoveryProgress());
      this.accountRecoverySubmittedTicketId.set(null);
      this.clearAccountRecoveryFormState();
    }
    this.applicationId.set(value);
    this.setItem(KEYS.applicationId, value);
  }

  clearApplicationId(): void {
    this.applicationId.set(null);
    this.removeItem(KEYS.applicationId);
  }

  setLastMileage(value: number): void {
    this.lastMileage.set(value);
    this.setItem(KEYS.lastMileage, String(value));
  }

  clearLastMileage(): void {
    this.lastMileage.set(null);
    this.removeItem(KEYS.lastMileage);
  }

  private readLastMileage(): number | null {
    const stored = this.getItem(KEYS.lastMileage);
    if (stored === null || !/^\d+$/.test(stored)) return null;
    const value = Number(stored);
    return Number.isFinite(value) && Number.isInteger(value) && value >= 0 ? value : null;
  }

  setFcm(value: string): void {
    this.fcm.set(value);
    this.setItem(KEYS.fcm, value);
  }

  clearFcm(): void {
    this.fcm.set(null);
    this.removeItem(KEYS.fcm);
  }

  setFlowType(value: FlowType): void {
    this.flowType.set(value);
    this.setItem(KEYS.flowType, value);
    if (value === 'account_recovery') {
      this.restoreAccountRecoveryStateForApplication(this.applicationId());
    }
  }

  setPath(value: RestorePath): void {
    this.selectedPath.set(value);
    this.setItem(KEYS.path, value);
  }

  setMainPhone(value: string): void {
    this.mainPhone.set(value);
    this.setItem(KEYS.mainPhone, value);
  }

  setAdditionalPhone(value: string): void {
    this.additionalPhone.set(value);
    this.setItem(KEYS.additionalPhone, value);
  }

  setNewPhone(value: string): void {
    this.newPhone.set(value);
    this.setItem(KEYS.newPhone, value);
  }

  setTicketId(value: string): void {
    this.ticketId.set(value);
    this.setItem(KEYS.ticketId, value);
  }

  setMainVerificationRequestId(value: string): void {
    this.mainVerificationRequestId.set(value);
    this.setItem(KEYS.mainVerificationRequestId, value);
  }

  setAdditionalVerificationRequestId(value: string): void {
    this.additionalVerificationRequestId.set(value);
    this.setItem(KEYS.additionalVerificationRequestId, value);
  }

  setAccountRecoveryTicketId(value: string): void {
    if (
      this.accountRecoveryTicketId() !== value ||
      this.accountRecoveryProgress().ticketId !== value
    ) {
      this.clearAccountRecoveryProgress();
    }
    this.accountRecoveryTicketId.set(value);
    this.removeItem(KEYS.accountRecoveryTicketId);
    this.removeLocalItem(KEYS.accountRecoveryTicketId);
    const ticketStorageKey = this.accountRecoveryTicketStorageKey(this.applicationId());
    if (ticketStorageKey) this.setLocalItem(ticketStorageKey, value);
  }

  markAccountRecoverySubmitted(ticketId: string): void {
    this.accountRecoverySubmittedTicketId.set(ticketId);
    this.setItem(ACCOUNT_RECOVERY_SUBMITTED_TICKET_KEY, ticketId);
  }

  hasSubmittedAccountRecoveryTicket(ticketId: string): boolean {
    return this.accountRecoverySubmittedTicketId() === ticketId;
  }

  setAccountRecoveryForm(form: ManagerRequestForm): void {
    const progress = {
      ...this.accountRecoveryProgress(),
      ticketId: this.accountRecoveryTicketId(),
      form,
    };
    this.accountRecoveryProgress.set(progress);
    this.persistAccountRecoveryProgress(progress);
  }

  setAccountRecoveryPhoneVerified(verified: boolean): void {
    const progress = {
      ...this.accountRecoveryProgress(),
      ticketId: this.accountRecoveryTicketId(),
      phoneVerified: verified,
    };
    this.accountRecoveryProgress.set(progress);
    this.persistAccountRecoveryProgress(progress);
  }

  setAccountRecoveryVerificationChannel(channel: 'sms' | 'call'): void {
    const ticketId = this.accountRecoveryTicketId();
    if (!ticketId) {
      this.clearAccountRecoveryProgress();
      return;
    }

    const progress = { ...this.accountRecoveryProgress(), ticketId, verificationChannel: channel };
    this.accountRecoveryProgress.set(progress);
    this.persistAccountRecoveryProgress(progress);
  }

  clearAccountRecoveryProgress(): void {
    this.accountRecoveryProgress.set(emptyAccountRecoveryProgress());
    this.clearAccountRecoveryFormState();
    this.removeAccountRecoveryProgressItem();
    this.clearAccountRecoverySubmittedTicket();
  }

  private clearAccountRecoveryFormState(): void {
    this.registeredPhone.set(null);
    this.contactPhone.set(null);
    this.formData.set(null);
  }

  private clearAccountRecoverySubmittedTicket(): void {
    this.accountRecoverySubmittedTicketId.set(null);
    this.removeItem(ACCOUNT_RECOVERY_SUBMITTED_TICKET_KEY);
  }

  clearAccountRecoveryTicketId(): void {
    this.accountRecoveryTicketId.set(null);
    this.removeItem(KEYS.accountRecoveryTicketId);
    const ticketStorageKey = this.accountRecoveryTicketStorageKey(this.applicationId());
    if (ticketStorageKey) this.removeLocalItem(ticketStorageKey);
    this.removeLocalItem(KEYS.accountRecoveryTicketId);
    this.clearAccountRecoveryProgress();
  }

  clear(): void {
    Object.values(KEYS).forEach((k) => this.removeItem(k));
    const ticketStorageKey = this.accountRecoveryTicketStorageKey(this.applicationId());
    if (ticketStorageKey) this.removeLocalItem(ticketStorageKey);
    this.removeLocalItem(KEYS.accountRecoveryTicketId);
    this.token.set(null);
    this.serviceId.set(null);
    this.applicationId.set(null);
    this.fcm.set(null);
    this.flowType.set(null);
    this.selectedPath.set(null);
    this.selectedCity.set(null);
    this.registeredPhone.set(null);
    this.contactPhone.set(null);
    this.formData.set(null);
    this.mainPhone.set(null);
    this.additionalPhone.set(null);
    this.newPhone.set(null);
    this.ticketId.set(null);
    this.mainVerificationRequestId.set(null);
    this.additionalVerificationRequestId.set(null);
    this.lastMileage.set(null);
    this.accountRecoveryTicketId.set(null);
    this.clearAccountRecoveryProgress();
    this.cachedTicket.set(null);
  }

  private restoreAccountRecoveryStateForApplication(applicationId: string | null): void {
    this.removeOtherAccountRecoveryTicketKeys(applicationId);
    const ticketStorageKey = this.accountRecoveryTicketStorageKey(applicationId);
    const restoredTicketId = ticketStorageKey ? this.getLocalItem(ticketStorageKey) : null;
    const progressTicketId = this.accountRecoveryProgress().ticketId;

    if (
      this.accountRecoveryTicketId() !== restoredTicketId ||
      progressTicketId !== restoredTicketId
    ) {
      this.clearPersistedAccountRecoveryProgress();
      this.accountRecoveryProgress.set(emptyAccountRecoveryProgress());
      this.accountRecoverySubmittedTicketId.set(null);
      this.clearAccountRecoveryFormState();
    }

    this.accountRecoveryTicketId.set(restoredTicketId);
  }
}
